<template>
  <div class="flex flex-col h-full min-h-0 bg-surface border-2 border-primary/20 rounded-xl shadow-md">
    <div class="flex items-start justify-between gap-2 p-4 border-b-2 border-primary/15 bg-primary-lighter/40 shrink-0">
      <div class="min-w-0">
        <h3 class="text-lg font-bold text-ink leading-tight truncate">
          {{ detail?.title ?? 'Activity detail' }}
        </h3>
        <p v-if="detail?.occurred_at" class="text-sm font-medium text-ink mt-1.5">
          {{ formatWhen(detail.occurred_at) }}
        </p>
        <p v-if="detail?.user" class="text-sm text-ink mt-2.5">
          <span class="font-bold text-ink">{{ detail.user.name }}</span>
          <span v-if="detail.user.role" class="text-ink-soft"> · {{ detail.user.role }}</span>
        </p>
      </div>
      <button
        type="button"
        class="shrink-0 text-primary/70 hover:text-primary-dark p-1.5 rounded-lg hover:bg-primary/10"
        aria-label="Close detail"
        @click="$emit('close')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto p-4 space-y-6">
      <div v-if="isLoading" class="flex flex-col items-center justify-center py-10 text-ink">
        <svg class="animate-spin h-8 w-8 text-primary mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        <span class="text-sm font-semibold">Loading details…</span>
      </div>

      <template v-else-if="isError">
        <p class="text-sm text-red-600">Could not load activity details.</p>
      </template>

      <p v-else-if="!detail" class="text-sm text-ink-soft">No details available for this entry.</p>

      <template v-else-if="detail">
        <p v-if="detail.description" class="text-sm text-ink leading-relaxed font-medium">
          {{ detail.description }}
        </p>

        <section
          v-if="factoryAssignedDetails"
          class="rounded-lg border-2 border-primary/15 bg-surface p-3 shadow-sm space-y-3"
        >
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide">Factory assignment</h4>
          <dl class="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div v-if="factoryAssignedDetails.factories.length">
              <dt class="text-xs font-semibold text-ink-soft">Factories</dt>
              <dd class="text-ink font-medium">{{ factoryAssignedDetails.factories.join(', ') }}</dd>
            </div>
            <div v-if="factoryAssignedDetails.orderAdminName">
              <dt class="text-xs font-semibold text-ink-soft">Order admin</dt>
              <dd class="text-ink font-medium">{{ factoryAssignedDetails.orderAdminName }}</dd>
            </div>
            <div v-if="factoryAssignedDetails.secondaryAdmins.length" class="sm:col-span-2">
              <dt class="text-xs font-semibold text-ink-soft">Secondary administrators</dt>
              <dd class="text-ink font-medium">{{ factoryAssignedDetails.secondaryAdmins.join(', ') }}</dd>
            </div>
          </dl>
        </section>

        <section
          v-if="orderAdminAssignmentDetails"
          class="rounded-lg border-2 border-primary/15 bg-primary-lighter/25 p-3 shadow-sm space-y-3"
        >
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide">Order admin assignment</h4>
          <dl class="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div v-if="orderAdminAssignmentDetails.previousAdmin">
              <dt class="text-xs font-semibold text-ink-soft">Previous</dt>
              <dd class="text-ink">{{ orderAdminAssignmentDetails.previousAdmin }}</dd>
            </div>
            <div v-if="orderAdminAssignmentDetails.newAdmin">
              <dt class="text-xs font-semibold text-ink-soft">Updated</dt>
              <dd class="text-ink font-medium">{{ orderAdminAssignmentDetails.newAdmin }}</dd>
            </div>
            <div v-if="orderAdminAssignmentDetails.activitySource">
              <dt class="text-xs font-semibold text-ink-soft">Activity source</dt>
              <dd class="text-ink font-medium">{{ orderAdminAssignmentDetails.activitySource }}</dd>
            </div>
            <div v-if="orderAdminAssignmentDetails.secondaryAdmins.length" class="sm:col-span-2">
              <dt class="text-xs font-semibold text-ink-soft">Secondary administrators</dt>
              <dd class="text-ink font-medium">{{ orderAdminAssignmentDetails.secondaryAdmins.join(', ') }}</dd>
            </div>
          </dl>
        </section>

        <!-- Context: metadata, factory, snapshots, structured payloads (before generic tables) -->
        <section
          v-if="displayedMetadataLines.length"
          class="rounded-lg border-2 border-primary/15 bg-primary-lighter/30 p-3 shadow-sm"
        >
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2.5">Details</h4>
          <dl class="space-y-2 text-sm">
            <div v-for="(row, idx) in displayedMetadataLines" :key="'m-' + idx" class="flex flex-col gap-0.5">
              <dt class="text-ink-soft text-xs font-semibold">{{ row.label }}</dt>
              <dd class="text-ink font-medium">{{ row.value }}</dd>
            </div>
          </dl>
        </section>

        <section
          v-if="detail.price_update"
          class="rounded-lg border-2 border-primary/15 p-3 bg-primary-lighter/25 shadow-sm"
        >
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2">Price change</h4>
          <div class="rounded-lg border border-primary/15 overflow-hidden">
            <table class="w-full text-sm">
              <thead class="bg-primary/10 text-left text-xs">
                <tr>
                  <th class="px-3 py-2 font-bold text-ink">Field</th>
                  <th class="px-3 py-2 font-bold text-ink">Previous</th>
                  <th class="px-3 py-2 font-bold text-ink">Updated</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(pr, pidx) in priceUpdateRows(detail.price_update)"
                  :key="'pu-' + pidx"
                  class="border-t border-primary/10 bg-surface align-top"
                >
                  <td class="px-3 py-2 text-ink font-medium whitespace-nowrap">{{ pr.label }}</td>
                  <td class="px-3 py-2 text-ink-soft tabular-nums">{{ pr.previous }}</td>
                  <td class="px-3 py-2 text-ink font-medium tabular-nums">{{ pr.updated }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section v-if="detail.customer_sample_cards?.length" class="space-y-4">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide">Design samples</h4>
          <div
            v-for="(card, cidx) in detail.customer_sample_cards"
            :key="'csc-' + cidx"
            class="rounded-lg border-2 border-primary/15 bg-primary-lighter/25 p-3 space-y-2 shadow-sm"
          >
            <div class="flex flex-wrap items-center gap-2 gap-y-1">
              <span
                v-if="card.outcome_label"
                class="inline-flex rounded-md px-2 py-0.5 text-xs font-bold uppercase tracking-wide border"
                :class="sampleOutcomeBadgeClass(card.outcome_label)"
              >
                {{ card.outcome_label }}
              </span>
              <span v-if="card.product_name" class="text-sm font-semibold text-ink">{{ card.product_name }}</span>
            </div>
            <p v-if="card.customer_note" class="text-sm text-ink whitespace-pre-wrap leading-relaxed">
              {{ card.customer_note }}
            </p>
            <div v-if="card.preview_images?.length" class="flex flex-wrap gap-3 pt-1">
              <figure
                v-for="(img, iidx) in card.preview_images"
                :key="'pi-' + cidx + '-' + iidx"
                class="text-center"
              >
                <img
                  :src="img.url"
                  :alt="img.label ?? 'Preview'"
                  class="w-28 h-28 object-cover rounded-lg border border-line mx-auto bg-surface"
                />
                <figcaption
                  v-if="img.label"
                  class="text-xs font-medium text-ink-soft mt-1 max-w-[128px] break-words mx-auto"
                >
                  {{ img.label }}
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <div
          v-if="detail.activity_kind === 'production_date_change' && detail.notify_customer !== undefined"
          class="rounded-lg border-2 px-3 py-2 text-sm font-medium"
          :class="
            detail.notify_customer
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-200 bg-amber-50 text-amber-950'
          "
        >
          {{
            detail.notify_customer
              ? 'Customer notification: Save & Notify was used.'
              : 'Customer notification: saved without notifying the customer.'
          }}
        </div>

        <section v-if="detail.factory" class="rounded-lg border-2 border-primary/15 p-3 bg-surface shadow-sm">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2">Factory</h4>
          <dl class="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt class="text-xs font-semibold text-ink-soft">Previous</dt>
              <dd class="font-medium text-ink">{{ detail.factory.previous_name }}</dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-ink-soft">Updated</dt>
              <dd class="font-medium text-ink">{{ detail.factory.new_name }}</dd>
            </div>
          </dl>
        </section>

        <section v-if="detail.shipping_snapshot" class="rounded-lg border-2 border-primary/15 p-3 bg-primary-lighter/25 shadow-sm">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2">Shipping address</h4>
          <div class="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <p class="text-xs font-bold text-ink uppercase tracking-wide mb-1">Previous</p>
              <p class="text-ink whitespace-pre-wrap">{{ detail.shipping_snapshot.previous_summary }}</p>
            </div>
            <div>
              <p class="text-xs font-bold text-ink uppercase tracking-wide mb-1">New</p>
              <p class="text-ink font-medium whitespace-pre-wrap">{{ detail.shipping_snapshot.new_summary }}</p>
            </div>
          </div>
        </section>

        <section v-if="detail.tracking_link" class="rounded-lg border-2 border-primary/15 p-3 bg-surface shadow-sm">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2">
            QC tracking link · {{ TRACKING_LINK_ACTION_LABEL[detail.tracking_link.action] }}
          </h4>
          <dl class="space-y-2 text-sm">
            <div v-if="detail.tracking_link.action !== 'added' && detail.tracking_link.previous_url">
              <dt class="text-xs font-semibold text-ink-soft">
                {{ detail.tracking_link.action === 'deleted' ? 'Deleted link' : 'Previous link' }}
              </dt>
              <dd>
                <a
                  :href="detail.tracking_link.previous_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary font-medium hover:underline text-xs break-all"
                >
                  {{ detail.tracking_link.previous_url }}
                </a>
              </dd>
            </div>
            <div v-if="detail.tracking_link.new_url">
              <dt class="text-xs font-semibold text-ink-soft">Updated link</dt>
              <dd>
                <a
                  :href="detail.tracking_link.new_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary font-medium hover:underline text-xs break-all"
                >
                  {{ detail.tracking_link.new_url }}
                </a>
              </dd>
            </div>
          </dl>
        </section>

        <section
          v-if="detail.techpack_pdf_urls"
          class="rounded-lg border-2 border-primary/15 p-3 bg-surface shadow-sm space-y-4"
        >
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide">Tech pack PDFs</h4>

          <p v-if="detail.techpack_pdf_urls.comparison_note" class="text-xs text-ink-soft">
            {{ detail.techpack_pdf_urls.comparison_note }}
          </p>

          <div
            v-if="
              detail.techpack_pdf_urls.merchant_generation_pending ||
              detail.techpack_pdf_urls.generation_pending
            "
            class="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950"
          >
            <template
              v-if="
                detail.techpack_pdf_urls.merchant_generation_pending &&
                detail.techpack_pdf_urls.generation_pending
              "
            >
              Merchant and order-level PDF regeneration pending.
            </template>
            <template v-else-if="detail.techpack_pdf_urls.merchant_generation_pending">
              Merchant tech pack PDF regeneration pending.
            </template>
            <template v-else> Tech pack PDF regeneration pending. </template>
          </div>

          <div class="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
            <div>
              <p class="text-xs font-bold text-ink uppercase tracking-wide mb-1">Merchant PDF · previous</p>
              <p class="text-xs break-all">
                <a
                  v-if="
                    techpackMerchantPreviousUrl(detail.techpack_pdf_urls) &&
                    showPdfLink(techpackMerchantPreviousUrl(detail.techpack_pdf_urls)!)
                  "
                  :href="techpackMerchantPreviousUrl(detail.techpack_pdf_urls)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary font-medium hover:underline"
                >
                  {{ techpackMerchantPreviousUrl(detail.techpack_pdf_urls) }}
                </a>
                <span
                  v-else-if="techpackMerchantPreviousUrl(detail.techpack_pdf_urls)"
                  class="text-ink"
                  >{{ techpackMerchantPreviousUrl(detail.techpack_pdf_urls) }}</span>
                <span v-else class="text-ink-mute">—</span>
              </p>
            </div>
            <div>
              <p class="text-xs font-bold text-ink uppercase tracking-wide mb-1">Merchant PDF · new</p>
              <p class="text-xs break-all">
                <a
                  v-if="
                    techpackMerchantNewUrl(detail.techpack_pdf_urls) &&
                    showPdfLink(techpackMerchantNewUrl(detail.techpack_pdf_urls)!)
                  "
                  :href="techpackMerchantNewUrl(detail.techpack_pdf_urls)"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary font-medium hover:underline"
                >
                  {{ techpackMerchantNewUrl(detail.techpack_pdf_urls) }}
                </a>
                <span
                  v-else-if="techpackMerchantNewUrl(detail.techpack_pdf_urls)"
                  class="text-ink font-medium"
                  >{{ techpackMerchantNewUrl(detail.techpack_pdf_urls) }}</span>
                <span v-else class="text-ink-mute">—</span>
              </p>
            </div>
          </div>

          <div v-if="detail.techpack_pdf_urls.factory_rows?.length" class="space-y-2">
            <p class="text-xs font-bold text-ink uppercase tracking-wide">Factory PDFs</p>
            <div class="rounded-lg border border-primary/15 overflow-hidden">
              <table class="w-full text-sm">
                <thead class="bg-primary/10 text-left text-xs">
                  <tr>
                    <th class="px-3 py-2 font-bold text-ink">Previous</th>
                    <th class="px-3 py-2 font-bold text-ink">New</th>
                    <th class="px-3 py-2 font-bold text-ink">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(fr, fidx) in detail.techpack_pdf_urls.factory_rows"
                    :key="'ftp-' + fidx"
                    class="border-t border-primary/10 bg-surface align-top"
                  >
                    <td class="px-3 py-2 text-ink">
                      <a
                        v-if="
                          factoryRowPreviousUrl(fr) && showPdfLink(factoryRowPreviousUrl(fr)!)
                        "
                        :href="factoryRowPreviousUrl(fr)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-primary font-medium hover:underline text-xs break-all"
                      >
                        {{ factoryRowPreviousUrl(fr) }}
                      </a>
                      <span v-else-if="factoryRowPreviousUrl(fr)" class="text-xs break-all">{{ factoryRowPreviousUrl(fr) }}</span>
                      <span v-else class="text-ink-mute">—</span>
                    </td>
                    <td class="px-3 py-2 text-ink font-medium">
                      <a
                        v-if="factoryRowNewUrl(fr) && showPdfLink(factoryRowNewUrl(fr)!)"
                        :href="factoryRowNewUrl(fr)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="text-primary font-medium hover:underline text-xs break-all"
                      >
                        {{ factoryRowNewUrl(fr) }}
                      </a>
                      <span v-else-if="factoryRowNewUrl(fr)" class="text-xs break-all">{{ factoryRowNewUrl(fr) }}</span>
                      <span v-else class="text-ink-mute">—</span>
                    </td>
                    <td class="px-3 py-2 text-ink-soft text-xs">
                      <span
                        v-if="fr.generation_pending"
                        class="inline-flex rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 font-medium text-amber-950"
                      >
                        Pending
                      </span>
                      <span v-else class="text-ink-mute">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section v-if="detail.reorder" class="rounded-lg border-2 border-primary/20 p-3 bg-primary-lighter/40 shadow-sm">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2">Reorder</h4>
          <dl class="space-y-1 text-sm">
            <div class="flex flex-wrap gap-x-2">
              <dt class="font-semibold text-ink-soft">Original order</dt>
              <dd class="font-medium text-ink">
                #{{ detail.reorder.original_order_no }}
                <span class="text-ink-soft font-normal">(ID {{ detail.reorder.original_order_id }})</span>
              </dd>
            </div>
          </dl>
        </section>

        <section v-if="detail.delivery_box" class="rounded-lg border-2 border-primary/15 p-3 bg-surface shadow-sm space-y-4">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide">
            {{ deliveryNoteSectionTitle(detail.delivery_box) }}
          </h4>

          <template v-if="isRichDeliveryNoteDetail(detail.delivery_box)">
            <p class="text-xs font-semibold text-ink-soft">{{ detail.delivery_box.action_label }}</p>

            <dl
              v-if="
                detail.delivery_box.total_items_qty !== undefined ||
                detail.delivery_box.not_included_items_qty !== undefined ||
                detail.delivery_box.items_qty_to_ship_total !== undefined
              "
              class="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2"
            >
              <div v-if="detail.delivery_box.total_items_qty !== undefined">
                <dt class="text-xs font-semibold text-ink-soft">Total items qty</dt>
                <dd class="font-medium text-ink">{{ detail.delivery_box.total_items_qty }}</dd>
              </div>
              <div v-if="detail.delivery_box.not_included_items_qty !== undefined">
                <dt class="text-xs font-semibold text-ink-soft">Not included items qty</dt>
                <dd class="font-medium text-ink">{{ detail.delivery_box.not_included_items_qty }}</dd>
              </div>
              <div v-if="detail.delivery_box.items_qty_to_ship_total !== undefined">
                <dt class="text-xs font-semibold text-ink-soft">Items qty to ship (total)</dt>
                <dd class="font-medium text-ink">{{ detail.delivery_box.items_qty_to_ship_total }}</dd>
              </div>
            </dl>

            <div v-if="detail.delivery_box.box_labels?.length" class="space-y-1.5">
              <p class="text-xs font-semibold text-ink-soft">Box labels</p>
              <ul class="flex flex-wrap gap-2">
                <li
                  v-for="(label, idx) in detail.delivery_box.box_labels"
                  :key="'bl-' + idx"
                  class="inline-flex items-center rounded-md border border-primary/20 bg-primary-lighter/40 px-2.5 py-1 text-xs font-medium text-ink"
                >
                  {{ label }}
                </li>
              </ul>
            </div>

            <div v-if="detail.delivery_box.delivery_notes_boxes?.length" class="space-y-2">
              <p class="text-xs font-bold text-ink uppercase tracking-wide">Notes ↔ packaging boxes</p>
              <div class="rounded-lg border border-primary/15 overflow-hidden">
                <table class="w-full text-sm">
                  <thead class="bg-primary/10 text-left text-xs">
                    <tr>
                      <th class="px-3 py-2 font-bold text-ink">Delivery note ID</th>
                      <th class="px-3 py-2 font-bold text-ink">Packaging box</th>
                      <th class="px-3 py-2 font-bold text-ink">Box ID</th>
                      <th class="px-3 py-2 font-bold text-ink">Display label</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, idx) in detail.delivery_box.delivery_notes_boxes"
                      :key="'dnb-' + idx"
                      class="border-t border-primary/10 bg-surface"
                    >
                      <td class="px-3 py-2 text-ink font-medium">{{ row.delivery_note_id }}</td>
                      <td class="px-3 py-2 text-ink">{{ row.packaging_box_label }}</td>
                      <td class="px-3 py-2 text-ink-soft font-mono text-xs">{{ row.packaging_box_id }}</td>
                      <td class="px-3 py-2 text-ink-soft break-words">{{ row.box_label }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="detail.delivery_box.new_custom_boxes?.length" class="space-y-2">
              <p class="text-xs font-bold text-ink uppercase tracking-wide">New custom boxes</p>
              <div class="rounded-lg border border-primary/15 overflow-hidden">
                <table class="w-full text-sm">
                  <thead class="bg-primary/10 text-left text-xs">
                    <tr>
                      <th class="px-3 py-2 font-bold text-ink">Label</th>
                      <th class="px-3 py-2 font-bold text-ink">W</th>
                      <th class="px-3 py-2 font-bold text-ink">H</th>
                      <th class="px-3 py-2 font-bold text-ink">L</th>
                      <th class="px-3 py-2 font-bold text-ink">Weight</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(row, idx) in detail.delivery_box.new_custom_boxes"
                      :key="'ncb-' + idx"
                      class="border-t border-primary/10 bg-surface"
                    >
                      <td class="px-3 py-2 text-ink font-medium">{{ row.label }}</td>
                      <td class="px-3 py-2 text-ink">{{ row.dimensions.width }}</td>
                      <td class="px-3 py-2 text-ink">{{ row.dimensions.height }}</td>
                      <td class="px-3 py-2 text-ink">{{ row.dimensions.length }}</td>
                      <td class="px-3 py-2 text-ink">{{ row.dimensions.weight }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>

          <dl v-else class="space-y-1 text-sm">
            <div>
              <dt class="text-xs font-semibold text-ink-soft">Box</dt>
              <dd class="font-medium text-ink">
                {{ detail.delivery_box.box_name ?? 'Box ' + detail.delivery_box.box_id }}
                <span class="text-ink-soft"> · ID {{ detail.delivery_box.box_id }}</span>
              </dd>
            </div>
            <div>
              <dt class="text-xs font-semibold text-ink-soft">Action</dt>
              <dd class="font-medium text-ink">{{ detail.delivery_box.action_label }}</dd>
            </div>
          </dl>
        </section>

        <section v-if="detail.comment" class="rounded-lg border-2 border-primary/15 p-3 bg-primary-lighter/30 shadow-sm">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2">
            Internal comment · {{ commentActionLabel(detail.comment.action) }}
          </h4>
          <p v-if="detail.comment.excerpt" class="text-sm text-ink whitespace-pre-wrap">{{ detail.comment.excerpt }}</p>
        </section>

        <section v-if="detail.reason" class="rounded-lg border-2 border-dashed border-primary/25 p-3 bg-surface">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-1">Reason</h4>
          <p class="text-sm text-ink">{{ detail.reason }}</p>
        </section>

        <section v-if="detail.field_changes?.length">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2.5">Field changes</h4>
          <div class="rounded-lg border-2 border-primary/15 overflow-hidden shadow-sm">
            <table class="w-full text-sm">
              <thead class="bg-primary/10 text-left text-xs">
                <tr>
                  <th class="px-3 py-2.5 font-bold text-ink">Field</th>
                  <th class="px-3 py-2.5 font-bold text-ink">Previous</th>
                  <th class="px-3 py-2.5 font-bold text-ink">New</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in detail.field_changes" :key="idx" class="border-t border-primary/10 bg-surface">
                  <td class="px-3 py-2.5 text-ink font-medium">{{ row.field_label }}</td>
                  <td class="px-3 py-2.5 text-ink-soft break-words">{{ row.previous_value }}</td>
                  <td class="px-3 py-2.5 text-ink font-medium break-words">{{ row.new_value }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <template v-for="(tbl, tidx) in displayedDetailTables" :key="'tbl-' + tidx">
          <section class="space-y-2">
            <h4 class="text-sm font-bold text-ink uppercase tracking-wide">{{ tbl.title }}</h4>
            <div class="rounded-lg border-2 border-primary/15 overflow-hidden shadow-sm">
              <table class="w-full text-sm">
                <thead class="bg-primary/10 text-left text-xs">
                  <tr>
                    <th v-for="(col, cidx) in tbl.columns" :key="'c-' + cidx" class="px-3 py-2.5 font-bold text-ink">
                      {{ col }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, ridx) in tbl.rows" :key="'r-' + ridx" class="border-t border-primary/10 bg-surface">
                    <td v-for="(cell, cidx) in row" :key="'cell-' + cidx" class="px-3 py-2.5 text-ink break-words">
                      {{ cell }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>

        <section
          v-if="detail.qc_pdf_report"
          class="rounded-lg border-2 border-primary/15 p-3 bg-surface shadow-sm"
        >
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2">QA report</h4>
          <a
            v-if="showPdfLink(detail.qc_pdf_report.href)"
            :href="detail.qc_pdf_report.href"
            target="_blank"
            rel="noopener noreferrer"
            class="text-primary font-medium hover:underline text-sm break-all"
          >
            Open QA report
          </a>
          <p v-else class="text-sm text-ink break-all">{{ detail.qc_pdf_report.url }}</p>
        </section>

        <section v-if="detail.pdf_changes?.length">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2.5">PDF updates</h4>
          <ul class="space-y-2">
            <li
              v-for="(pdf, idx) in detail.pdf_changes"
              :key="idx"
              class="text-sm rounded-lg border-2 border-primary/15 p-3 bg-primary-lighter/40"
            >
              <p v-if="pdf.label" class="font-bold text-ink mb-2">{{ pdf.label }}</p>

              <div
                v-if="
                  isCustomPdfActivity(detail) &&
                  (previewableImageUrl(pdf.previous_url) || previewableImageUrl(pdf.new_url))
                "
                class="flex flex-wrap gap-4 mb-3"
              >
                <figure v-if="previewableImageUrl(pdf.previous_url)" class="text-center max-w-[min(100%,24rem)]">
                  <img
                    :src="pdf.previous_url"
                    alt=""
                    class="max-h-44 max-w-full object-contain rounded-lg border border-line bg-surface shadow-sm mx-auto"
                    loading="lazy"
                  />
                  <figcaption class="text-xs font-medium text-ink-soft mt-1.5">Previous file</figcaption>
                </figure>
                <figure v-if="previewableImageUrl(pdf.new_url)" class="text-center max-w-[min(100%,24rem)]">
                  <img
                    :src="pdf.new_url"
                    alt=""
                    class="max-h-44 max-w-full object-contain rounded-lg border border-line bg-surface shadow-sm mx-auto"
                    loading="lazy"
                  />
                  <figcaption class="text-xs font-medium text-ink-soft mt-1.5">New file</figcaption>
                </figure>
              </div>

              <div class="flex flex-col gap-1.5">
                <a
                  v-if="showPdfLink(pdf.previous_url)"
                  :href="pdf.previous_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary font-medium hover:underline text-xs break-all"
                >
                  Previous PDF
                </a>
                <a
                  v-if="showPdfLink(pdf.new_url)"
                  :href="pdf.new_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary font-medium hover:underline text-xs break-all"
                >
                  New PDF
                </a>
              </div>
            </li>
          </ul>
        </section>

        <section v-if="detail.approved_images?.length">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2.5">
            {{ approvedImagesHeading(detail) }}
          </h4>
          <div class="flex flex-wrap gap-3">
            <figure v-for="(img, idx) in detail.approved_images" :key="'a-' + idx" class="text-center">
              <img
                :src="img.url"
                :alt="img.label ?? (detail.action_type === 'submitted_for_customer_review' ? 'Uploaded sample' : 'Approved')"
                class="w-24 h-24 object-cover rounded-lg border border-line mx-auto"
              />
              <figcaption v-if="img.label" class="text-xs font-medium text-ink mt-1 max-w-[120px]">{{ img.label }}</figcaption>
            </figure>
          </div>
        </section>

        <section v-if="detail.rejected_images?.length">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2.5">Rejected images</h4>
          <div class="flex flex-wrap gap-3">
            <figure v-for="(img, idx) in detail.rejected_images" :key="'r-' + idx" class="text-center">
              <img :src="img.url" :alt="img.label ?? 'Rejected'" class="w-24 h-24 object-cover rounded-lg border border-red-100 mx-auto" />
              <figcaption v-if="img.label" class="text-xs font-medium text-ink mt-1 max-w-[120px]">{{ img.label }}</figcaption>
            </figure>
          </div>
        </section>

        <section v-if="detail.previous_state?.length || detail.new_state?.length">
          <h4 class="text-sm font-bold text-ink uppercase tracking-wide mb-2.5">State</h4>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div v-if="detail.previous_state?.length" class="rounded-lg border-2 border-primary/15 bg-primary-lighter/30 p-3">
              <p class="text-xs font-bold text-ink mb-2 uppercase tracking-wide">Previous</p>
              <dl class="space-y-2 text-sm">
                <div v-for="(s, idx) in detail.previous_state" :key="'p-' + idx" class="flex flex-col gap-0.5">
                  <dt class="text-ink-soft text-xs font-semibold">{{ s.label }}</dt>
                  <dd class="text-ink font-medium">{{ s.value }}</dd>
                </div>
              </dl>
            </div>
            <div v-if="detail.new_state?.length" class="rounded-lg border-2 border-primary/25 bg-primary-lighter/50 p-3">
              <p class="text-xs font-bold text-ink mb-2 uppercase tracking-wide">New</p>
              <dl class="space-y-2 text-sm">
                <div v-for="(s, idx) in detail.new_state" :key="'n-' + idx" class="flex flex-col gap-0.5">
                  <dt class="text-ink-soft text-xs font-semibold">{{ s.label }}</dt>
                  <dd class="text-ink font-medium">{{ s.value }}</dd>
                </div>
              </dl>
            </div>
          </div>
        </section>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  OrderActivityDeliveryBox,
  OrderActivityDetailTable,
  OrderActivityFactoryTechpackPdfRow,
  OrderActivityLogDetail,
  OrderActivityMetadataLine,
  OrderActivityPriceBreakdownSnapshot,
  OrderActivityPriceUpdateDetail,
  OrderActivityTechpackPdfUrlsUpdate,
  OrderActivityTrackingLink,
} from '@/models/OrderActivityLog';
import { computed } from 'vue';

const TRACKING_LINK_ACTION_LABEL: Record<OrderActivityTrackingLink['action'], string> = {
  added: 'Added',
  edited: 'Edited',
  deleted: 'Deleted',
};

const props = defineProps<{
  detail: OrderActivityLogDetail | null | undefined;
  isLoading: boolean;
  isError: boolean;
}>();

/** Metadata rows that are raw IDs or counts of IDs — omit from the Details list. */
const METADATA_LABEL_HIDE: RegExp[] = [
  /^order item id$/i,
  /^items updated$/i,
  /^factory product id$/i,
  /^added factory product ids$/i,
  /^source order item$/i,
  /^samples approved \(ids\)$/i,
  /^samples skipped \(ids\)$/i,
];

/** Hide “Quote order” when not a quote (mapper sends Yes/No). */
function visibleMetadataLines(lines: OrderActivityMetadataLine[] | undefined): OrderActivityMetadataLine[] {
  if (!lines?.length) return [];
  return lines.filter((row) => {
    const label = String(row.label).trim();
    if (METADATA_LABEL_HIDE.some((re) => re.test(label))) return false;
    if (row.label !== 'Quote order') return true;
    const v = String(row.value).trim().toLowerCase();
    return v === 'yes' || v === 'true';
  });
}

const displayedMetadataLines = computed(() => visibleMetadataLines(props.detail?.metadata_lines));

type DetailMetaRecord = Record<string, unknown>;

function isRecord(value: unknown): value is DetailMetaRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseJsonText(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function toStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((entry) => (entry === null || entry === undefined ? '' : String(entry).trim()))
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      return toStringList(parseJsonText(trimmed));
    }
    return trimmed
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  if (value === null || value === undefined) return [];
  return [String(value)];
}

function firstString(value: unknown): string | undefined {
  const list = toStringList(value);
  return list.length ? list[0] : undefined;
}

function toNumberLike(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function lineValueByLabel(lines: OrderActivityMetadataLine[] | undefined, labels: RegExp[]): string | undefined {
  if (!lines?.length) return undefined;
  const row = lines.find((line) => labels.some((re) => re.test(String(line.label).trim())));
  return row?.value;
}

const rawActivityMeta = computed<DetailMetaRecord | undefined>(() => {
  const raw = (props.detail as unknown as { activity_meta_data?: unknown } | undefined)?.activity_meta_data;
  return isRecord(raw) ? raw : undefined;
});

const factoryAssignedDetails = computed<{
  factories: string[];
  orderAdminName?: string;
  secondaryAdmins: string[];
} | null>(() => {
  if (props.detail?.action_type !== 'factory_assigned') return null;

  const meta = rawActivityMeta.value;
  const lines = props.detail.metadata_lines;
  const factories = [
    ...new Set(
      [
        ...toStringList(meta?.new_factory_names),
        ...toStringList(meta?.new_factory_name),
        ...toStringList(lineValueByLabel(lines, [/^factories$/i, /^factory$/i, /^new factory names?$/i])),
      ].filter(Boolean),
    ),
  ];
  const secondaryAdmins = [
    ...new Set(
      [
        ...toStringList(meta?.secondary_administrator_names),
        ...toStringList(meta?.secondary_order_admin_names),
        ...toStringList(
          lineValueByLabel(lines, [
            /^secondary administrator names?$/i,
            /^secondary order admin names?$/i,
            /^secondary administrators$/i,
          ]),
        ),
      ].filter(Boolean),
    ),
  ];

  const orderAdminName =
    firstString(meta?.order_admin_name) ??
    firstString(lineValueByLabel(lines, [/^order admin$/i, /^order admin name$/i]));

  if (!factories.length && !orderAdminName && !secondaryAdmins.length) {
    return null;
  }

  return {
    factories,
    orderAdminName,
    secondaryAdmins,
  };
});

const orderAdminAssignmentDetails = computed<{
  previousAdmin?: string;
  newAdmin?: string;
  activitySource?: string;
  secondaryAdmins: string[];
} | null>(() => {
  if (props.detail?.action_type !== 'order_admin_assigned') return null;

  const meta = rawActivityMeta.value;
  const lines = props.detail.metadata_lines;
  const fieldRow = props.detail.field_changes?.find((row) => row.field_label === 'Order admin');
  const previousFromField = fieldRow?.previous_value && fieldRow.previous_value !== '—' ? fieldRow.previous_value : undefined;
  const newFromField = fieldRow?.new_value && fieldRow.new_value !== '—' ? fieldRow.new_value : undefined;

  const previousAdmin =
    previousFromField ??
    firstString(meta?.previous_order_admin_name) ??
    firstString(lineValueByLabel(lines, [/^previous order admin$/i, /^previous order admin name$/i]));
  const newAdmin =
    newFromField ??
    firstString(meta?.new_order_admin_name) ??
    firstString(lineValueByLabel(lines, [/^new order admin$/i, /^new order admin name$/i, /^order admin$/i]));
  const activitySource = firstString(lineValueByLabel(lines, [/^activity source$/i])) ?? firstString(meta?.activity_source);
  const secondaryAdmins = [
    ...new Set(
      [
        ...toStringList(meta?.secondary_administrator_names),
        ...toStringList(meta?.secondary_order_admin_names),
        ...toStringList(
          lineValueByLabel(lines, [
            /^secondary administrator names?$/i,
            /^secondary order admin names?$/i,
            /^secondary administrators$/i,
          ]),
        ),
      ].filter(Boolean),
    ),
  ];

  if (!previousAdmin && !newAdmin && !activitySource && !secondaryAdmins.length) return null;

  return {
    previousAdmin,
    newAdmin,
    activitySource,
    secondaryAdmins,
  };
});

const DETAIL_TABLE_COLUMN_HIDE: RegExp[] = [
  /^product id$/i,
  /^order item id$/i,
  /^factory product id$/i,
];

/** Drop ID-only columns from mapper-built tables (e.g. approval “Product ID”). */
function detailTablesWithoutIdColumns(tables: OrderActivityDetailTable[] | undefined): OrderActivityDetailTable[] {
  if (!tables?.length) return [];
  const out: OrderActivityDetailTable[] = [];
  for (const tbl of tables) {
    const keepIdx = tbl.columns
      .map((col, i) => ({ col: String(col).trim(), i }))
      .filter(({ col }) => !DETAIL_TABLE_COLUMN_HIDE.some((re) => re.test(col)))
      .map(({ i }) => i);
    if (keepIdx.length === 0) continue;
    if (keepIdx.length === tbl.columns.length) {
      out.push(tbl);
      continue;
    }
    const columns = keepIdx.map((i) => tbl.columns[i]);
    const rows = tbl.rows.map((row) => keepIdx.map((i) => row[i] ?? ''));
    out.push({ ...tbl, columns, rows });
  }
  return out;
}

const displayedDetailTables = computed(() => detailTablesWithoutIdColumns(props.detail?.detail_tables));

const PRICE_FIELD_ORDER: (keyof OrderActivityPriceBreakdownSnapshot)[] = [
  'total_price',
  'total_quantity',
  'order_item_final_price',
  'order_final_price',
  'total_final_price',
  'discount',
  'shipping',
  'fixed_value',
  'is_fixed',
];

const PRICE_FIELD_LABELS: Record<keyof OrderActivityPriceBreakdownSnapshot, string> = {
  total_price: 'Total price',
  total_quantity: 'Total quantity',
  order_item_final_price: 'Order item final price',
  order_final_price: 'Order final price',
  total_final_price: 'Total final price',
  discount: 'Discount',
  shipping: 'Shipping',
  fixed_value: 'Fixed value',
  is_fixed: 'Fixed pricing',
};

function formatMoneyAmount(n: number): string {
  const rounded = Math.round((n + Number.EPSILON) * 100) / 100;
  return rounded.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatPriceSnapshotCell(
  key: keyof OrderActivityPriceBreakdownSnapshot,
  v: number | boolean | undefined,
): string {
  if (v === undefined) return '—';
  if (key === 'is_fixed') return typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v);
  if (key === 'total_quantity') return String(Math.round(Number(v)));
  if (typeof v === 'number') return formatMoneyAmount(v);
  return String(v);
}

function priceUpdateRows(pu: OrderActivityPriceUpdateDetail): { label: string; previous: string; updated: string }[] {
  const prev = pu.previous;
  const next = pu.updated;
  const keys = new Set<keyof OrderActivityPriceBreakdownSnapshot>();
  (Object.keys(prev) as (keyof OrderActivityPriceBreakdownSnapshot)[]).forEach((k) => keys.add(k));
  (Object.keys(next) as (keyof OrderActivityPriceBreakdownSnapshot)[]).forEach((k) => keys.add(k));
  const ordered: (keyof OrderActivityPriceBreakdownSnapshot)[] = [];
  for (const k of PRICE_FIELD_ORDER) {
    if (keys.has(k)) ordered.push(k);
  }
  for (const k of keys) {
    if (!ordered.includes(k)) ordered.push(k);
  }
  const rows: { label: string; previous: string; updated: string }[] = [];
  for (const key of ordered) {
    const pv = prev[key];
    const nv = next[key];
    const ps = formatPriceSnapshotCell(key, pv);
    const ns = formatPriceSnapshotCell(key, nv);
    if (ps === '—' && ns === '—') continue;
    rows.push({
      label: PRICE_FIELD_LABELS[key] ?? String(key),
      previous: ps,
      updated: ns,
    });
  }
  return rows;
}

defineEmits<{
  close: [];
}>();

/** e.g. 25 Jan 2026, 13:05 (local) */
function formatWhen(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const pad = (n: number) => String(n).padStart(2, '0');
  const datePart = `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  const timePart = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${datePart}, ${timePart}`;
}

function commentActionLabel(action: string) {
  const map: Record<string, string> = {
    added: 'Added',
    edited: 'Edited',
    deleted: 'Deleted',
  };
  return map[action] ?? action;
}

function approvedImagesHeading(detail: OrderActivityLogDetail) {
  return detail.action_type === 'submitted_for_customer_review' ? 'Uploaded Samples' : 'Approved images';
}

function sampleOutcomeBadgeClass(label: string) {
  const u = label.toLowerCase();
  if (u === 'approved') return 'bg-emerald-100 text-emerald-900 border-emerald-200';
  if (u === 'rejected') return 'bg-red-50 text-red-900 border-red-200';
  if (u === 'submitted') return 'bg-sky-50 text-sky-900 border-sky-200';
  return 'bg-primary-lighter/60 text-ink border-primary/20';
}

const STORAGE_BASE = (import.meta.env.VITE_APP_FILE_URL ?? '').toString().replace(/\/+$/, '');

/**
 * Absolute URL for display and `<a href>` — joins `VITE_APP_FILE_URL` when the API sends storage-relative paths.
 * Uses mapper `href` when already absolute, otherwise the raw path.
 */
function resolvedStorageFileUrl(hrefFromMapper?: string | null, relativePath?: string | null): string | undefined {
  const resolveOne = (raw: string | null | undefined): string | undefined => {
    const s = typeof raw === 'string' ? raw.trim() : '';
    if (!s) return undefined;
    if (/^https?:\/\//i.test(s)) return s;
    if (STORAGE_BASE) return `${STORAGE_BASE}/${s.replace(/^\/+/, '')}`;
    return s;
  };
  return resolveOne(hrefFromMapper) ?? resolveOne(relativePath);
}

function techpackMerchantPreviousUrl(tp: OrderActivityTechpackPdfUrlsUpdate) {
  return resolvedStorageFileUrl(tp.previous_merchant_pdf_href, tp.previous_merchant_pdf_url);
}

function techpackMerchantNewUrl(tp: OrderActivityTechpackPdfUrlsUpdate) {
  return resolvedStorageFileUrl(tp.new_merchant_pdf_href, tp.new_merchant_pdf_url);
}

function factoryRowPreviousUrl(fr: OrderActivityFactoryTechpackPdfRow) {
  return resolvedStorageFileUrl(fr.previous_pdf_href, fr.previous_pdf_url);
}

function factoryRowNewUrl(fr: OrderActivityFactoryTechpackPdfRow) {
  return resolvedStorageFileUrl(fr.new_pdf_href, fr.new_pdf_url);
}

/** Hide placeholder / empty anchors (e.g. `#`, em dash) */
function showPdfLink(url: string) {
  return Boolean(url && url !== '#' && url !== '—');
}

const IMAGE_FILE_RE = /\.(jpe?g|png|gif|webp|svg|bmp|avif)(\?|#|$)/i;

/** Custom PDF uploaded / removed — used to gate image previews */
function isCustomPdfActivity(detail: OrderActivityLogDetail): boolean {
  return (
    detail.activity_kind === 'custom_pdf' ||
    (typeof detail.action_type === 'string' && detail.action_type.startsWith('custom_pdf'))
  );
}

/** Whether URL looks like an image we can preview in `<img>` */
function previewableImageUrl(url: string | undefined): boolean {
  if (!url || !showPdfLink(url)) return false;
  try {
    const pathOnly = url.includes('://') ? new URL(url).pathname : url;
    const base = pathOnly.split(/[?#]/)[0] ?? '';
    return IMAGE_FILE_RE.test(base);
  } catch {
    return IMAGE_FILE_RE.test(url);
  }
}

/** Expanded delivery-module payload (vs. legacy single delivery_note_id only). */
function isRichDeliveryNoteDetail(box: OrderActivityDeliveryBox): boolean {
  if (box.was_split === true) return true;
  if ((box.created_delivery_note_ids?.length ?? 0) > 0) return true;
  return (
    (box.new_custom_boxes?.length ?? 0) > 0 ||
    (box.delivery_notes_boxes?.length ?? 0) > 0 ||
    (box.box_labels?.length ?? 0) > 0 ||
    box.total_items_qty !== undefined ||
    box.not_included_items_qty !== undefined ||
    box.items_qty_to_ship_total !== undefined
  );
}

function deliveryNoteSectionTitle(box: OrderActivityDeliveryBox): string {
  const many =
    (box.delivery_note_ids?.length ?? 0) > 1 ||
    (box.created_delivery_note_ids?.length ?? 0) > 1 ||
    (box.delivery_notes_boxes?.length ?? 0) > 1;
  if (isRichDeliveryNoteDetail(box)) {
    return many ? 'Delivery notes' : 'Delivery note';
  }
  return 'Delivery box';
}
</script>
